<!-- eslint-disable vue/no-v-html -->
<template>
  <MultiStepForm :steps="blocks" :init-step="initStep" class="min-h-screen" render-mode="visited">
    <template #landing="{ nextStep }">
      <template v-if="drupal">
        <LandingBlock
          :image="imageUrl"
          :image-alt="drupal.view.imageAlt"
          :title="drupal.view.title"
          :subtitle="drupal.view.subtitle"
          :description="drupal.view.leadHtml"
          class="lg:mb-7"
          :cta="true"
          :theme="THEMES.DJURENS_RATT"
          @on-next="nextStep"
        />
        <!-- The first row sits where Contentful's motivation block did, the rest as its misc block. -->
        <LandingBlock
          v-for="(row, i) in drupal.view.rows"
          :key="i"
          :image="row.image"
          :image-alt="row.imageAlt"
          :title="row.title"
          :subtitle="row.eyebrow"
          :body-html="row.bodyHtml"
          :reverse="i === 0"
          :class="i === 0 ? 'z-10 -mt-4 xl:mb-6 xl:mt-0' : 'mb-7'"
          :button-label="row.button"
          :is-third-block="Boolean(row.link)"
          :theme="THEMES.DJURENS_RATT"
          @on-next="thirdBlockNavigate(row.link)"
        />
      </template>
      <template v-else>
        <LandingBlock
          :image="imageUrl"
          :title="document?.title"
          :subtitle="document?.subtitle"
          :description="document?.description"
          class="lg:mb-7"
          :cta="true"
          :theme="THEMES.DJURENS_RATT"
          @on-next="nextStep"
        />
        <LandingBlock
          :image="
            document?.cloudinaryMotivationImage ? document?.cloudinaryMotivationImage[0]?.url : ''
          "
          :title="document?.motivationTitle"
          :subtitle="document?.motivationSubtitle"
          :description="document?.motivation"
          :reverse="true"
          class="z-10 -mt-4 xl:mb-6 xl:mt-0"
          :theme="THEMES.DJURENS_RATT"
        />
        <LandingBlock
          v-if="SITE_ORG === 'djurensratt' && document?.miscTitle"
          class="mb-7"
          :image="document?.cloudinaryMiscImage ? document?.cloudinaryMiscImage[0]?.url : ''"
          :title="document.miscTitle"
          :subtitle="document.miscSubtitle"
          :description="document.miscDescription"
          :button-label="document.miscButtonText"
          :theme="THEMES.DJURENS_RATT"
          :is-third-block="true"
          @on-next="thirdBlockNavigate(document?.miscLink)"
        />
      </template>
    </template>

    <template #sum="{ nextStep }">
      <FormWrapper :title="$t('steps.sum.header')" :image="imageUrl" :theme="THEMES.DJURENS_RATT">
        <DonationStandard
          v-model:sum="sum"
          :min-sum="minSum"
          :sums="sums"
          :v$="v$.sum as any"
          :image="imageUrl"
          :currency="currency"
          :selected-toggle-tab="1"
          @on-next="nextStep"
        />
      </FormWrapper>
    </template>

    <template #present="{ nextStep }">
      <GiftSelection
        v-model:present="present"
        v-model:skip-present="skipPresent"
        :v$="v$.gift as any"
        :title="t('steps.present.header')"
        :sub-title="t('steps.present.subtitle')"
        :presents="presents"
        :theme="THEMES.DJURENS_RATT"
        :tall-cards="false"
        @on-next="nextStep"
      />
    </template>

    <template #details="{ nextStep }">
      <FormWrapper
        :title="$t('steps.details.header')"
        :image="imageUrl"
        :sub-title="$t('personal-details.title')"
        :theme="THEMES.DJURENS_RATT"
      >
        <PersonalDetails
          v-model:person="person"
          v-model:masked="masked"
          :use-ssn="true"
          :ssn-required="true"
          :require-legal-age="true"
          :org="config.public.SITE_ORG"
          :v$="v$.personDetails as any"
          :site-org="SITE_ORG"
          @on-next="nextStep"
        />
        <div
          v-if="drupal?.view.consentHtml"
          class="mt-6 font-sans text-sm [&_a]:underline"
          v-html="drupal.view.consentHtml"
        />
      </FormWrapper>
    </template>

    <template #address>
      <FormWrapper
        :title="$t('steps.address.header')"
        :sub-title="
          person.useLookup ? $t('postal-address.retrieved-data') : $t('personal-details.title')
        "
        :image="imageUrl"
        :theme="THEMES.DJURENS_RATT"
      >
        <PostalAddress
          v-model:person="person"
          v-model:masked="masked"
          v-model:save-postal-address="savePostalAddress"
          :show-city="false"
          :show-country="false"
          :v$="v$.personPostal as any"
          :site-org="SITE_ORG"
          :next-text="t('button.to-signing')"
          :is-last-step="true"
          @on-next="handleForm"
        />
      </FormWrapper>
    </template>
  </MultiStepForm>
</template>

<script setup lang="ts">
import { ref, computed, defineAsyncComponent } from 'vue'
import { MultiStepForm, THEMES, type ISteps } from 'refresh-ui'
import { zPerson } from 'shared-types'
import { useVuelidate } from '@vuelidate/core'
import { useI18n } from 'vue-i18n'
import { withQuery } from 'ufo'
import { firstValues, qsString, zFormUrls } from 'refresh-common'
import LandingBlock from 'act-components/src/components/elements/LandingBlock.vue'
import { cloudinaryStillUrl } from 'act-components/src/libs/cloudinaryVideo'
import { flagError } from 'error-handling'
import type { SiteLang, SiteOrg, Environment } from '~/types/env'
import { useFormLoader } from '~/composables/useFormLoader'
import { usePrefillPerson } from '~/composables/usePrefillPerson'
import { getSiteDefaults } from '~/libs/defaults'
import type { MembershipDocument, ResultDocument } from '~/types/cms'
import { FormType } from '~/types/forms'
import { zSsnLookupRes } from '~/libs/apiSchema'
import { thirdBlockNavigate } from '~/libs/urlTools'
import { commonRules, presentRules } from '~/libs/rules'
import { isNoPresent } from '~/libs/presents'
import { useDrupalMonthly } from '~/composables/useDrupalMonthly'
import {
  contentfulFormId,
  formCacheTags,
  formsMode,
  FORMS_OPTIN_PARAM,
  FORMS_OPTIN_VALUE,
  isFormsOptIn,
  monthlySlug,
} from '~/libs/formsDrupal'

// Lazy-loaded to reduce initial payload; these blocks are rendered later in the form flow.
const FormWrapper = defineAsyncComponent(
  () => import('act-components/src/components/elements/FormWrapper.vue'),
)
const PersonalDetails = defineAsyncComponent(
  () => import('act-components/src/components/elements/PersonalDetails.vue'),
)
const PostalAddress = defineAsyncComponent(
  () => import('act-components/src/components/elements/PostalAddress.vue'),
)
const DonationStandard = defineAsyncComponent(
  () => import('act-components/src/components/elements/DonationStandard.vue'),
)
const GiftSelection = defineAsyncComponent(
  () => import('act-components/src/components/elements/GiftSelection.vue'),
)

const route = useRoute()
const config = useRuntimeConfig()

const { t } = useI18n()
const { showLoader, hasResult } = useFormLoader()
const { $rollbar } = useNuxtApp()

definePageMeta({
  layout: 'form',
})

/** Handle env vars  */
const { SITE_ORG, SITE_LANG, ENVIRONMENT } = config.public as {
  SITE_ORG: SiteOrg
  SITE_LANG: SiteLang
  ENVIRONMENT: Environment
}

if (SITE_ORG !== 'djurensratt')
  throw createError({
    status: 404,
    statusText: t('error.404.org'),
    data: {
      buttonText: t('error.404.altOrg'),
      buttonUrl: '/monthly',
    },
  })

// Validate and parse query params
// NOTE: ALL query params used must be set up here
// `present` becomes Premieval on CreateMember2, so it has to be duplicate-safe for the
// same reason campaignCode is. `handedOut` inherits zFormUrls rather than redeclaring it.
const zMonthly = zFormUrls.extend({
  present: qsString().prefault(''),
})
const params = ref(zMonthly.parse(route.query))

const defaultSlug = getSiteDefaults({
  lang: SITE_LANG,
  env: ENVIRONMENT,
}).defaultSlug

const slug = (route?.params?.slug as string) || defaultSlug
const basePath = route.path.split('/')[1]

/** Set up steps */
const blocks = computed<ISteps>(() => ({
  landing: {
    name: 'landing',
    title: t('steps.landing.title'),
    visible: true,
  },
  sum: {
    name: 'sum',
    title: t('steps.sum.title'),
    visible: true,
  },
  present: {
    name: 'present',
    title: t('steps.present.title'),
    visible: !params.value.handedOut && presents.value.length > 0,
  },
  details: {
    name: 'details',
    title: t('steps.details.title'),
    visible: true,
  },
  address: {
    name: 'address',
    title: t('steps.address.title'),
    visible: true,
  },
}))
const { initStep: defaultStep, debugSteps, isDebug, isPreview } = useStepNavigation(blocks)

// `?forms=drupal` opts a staff test into Drupal while FORMS_DRUPAL is `optin`; it travels on to
// /medgivande and the result URLs. Drupal's slugs are lowercase, so `REAK` reads `reak`.
const optedIn = isFormsOptIn(route.query)
const formsOn = formsMode(config.public.FORMS_DRUPAL) !== 'off'
// The catch-all route's param is an array of segments; a form slug is one segment.
const slugText = [slug].flat().join('/')
const drupal = await useDrupalMonthly(slugText, SITE_LANG, isPreview.value, optedIn)
// With FORMS_DRUPAL off `?forms=drupal` changes nothing, so it doesn't change indexing either.
const noindex = (formsOn && optedIn) || Boolean(drupal?.view.noindex)
if (import.meta.server && formsOn) {
  useResponseHeader('Netlify-Cache-Tag').value = formCacheTags(
    SITE_LANG,
    monthlySlug(slugText),
    'monthly',
  )
  useResponseHeader('Netlify-Vary').value = 'query'
  if (noindex) useResponseHeader('X-Robots-Tag').value = 'noindex'
}

/** Get cms data */
const document = drupal
  ? undefined
  : await useContentfulData<MembershipDocument>(
      slug,
      'interaction', // content type
      isPreview.value,
      t,
    )
const formId = drupal ? drupal.formId : contentfulFormId(slugText)
// The form the server counts this sign-up under (form stats); a debug-mode one counts nowhere.
const stats = isDebug.value ? undefined : { formId, formLang: drupal?.formLang ?? SITE_LANG }
if (import.meta.server && formsOn)
  console.info(`[forms] manadsgivare ${slugText}: ${drupal ? 'drupal' : 'contentful'} ${formId}`)

// Check if form is closed
const { checkFormClosed } = useFormStatus()
checkFormClosed(drupal ? drupal.view.isClosed : document?.isClosed, basePath)

const presents = computed(() =>
  drupal
    ? drupal.view.presents.map(present => ({
        label: present.label,
        value: present.key,
        image: present.image,
        description: present.description,
        categories: [],
      }))
    : document?.present?.map((present: any) => ({
        label: present?.fields?.label,
        value: present?.fields?.key,
        image: present?.fields?.cloudinaryMedia ? present?.fields?.cloudinaryMedia[0]?.url : '',
        description: present?.fields?.description,
        categories: present?.fields?.categories?.map((category: string) => category) || [],
      })) || [],
)
// A Drupal present's value is its key; the CRM records its Mysoft code.
const presentCode = (key: string) =>
  drupal ? (drupal.view.presents.find(present => present.key === key)?.mysoftCode ?? '') : key
// `blocks` reads `presents` (via the eager `defaultStep.value` snapshot below), so the
// initial step must be captured only after `presents` is initialized — otherwise the
// `blocks` getter dereferences `presents` while it is still in its temporal dead zone.
const initStep = ref(defaultStep.value)
const present = ref(
  (drupal ? drupal.view.presents[0]?.key : document?.present?.[0]?.fields?.key) || '',
)
const skipPresent = ref(false)

const sums = drupal ? drupal.view.sums : (document?.sums ?? [])
const sum = ref(params.value.sum || (sums[0] as number))

const campaignCode = ref(
  params.value.campaignCode ||
    (drupal
      ? drupal.view.recruitmentCode || undefined
      : document?.recruitmentCode?.fields?.recruitmentCode),
)

const formStatus = ref('idle') as Ref<'idle' | 'loading' | 'success' | 'error'>
const { reportError } = useFormErrorHandler()

const formDefaults = useFormDefaults({
  siteLang: SITE_LANG,
  siteOrg: SITE_ORG,
  formType: FormType.MONTHLY,
})

type Currency = 'sek' | 'eur' | 'usd'
const currency: Currency = SITE_ORG === 'djurensratt' ? 'sek' : 'sek' // TODO: Update with other currency when available

// Raw, so the components below can still tell a clip from a still. `Image` swaps in the poster.
const imageUrl = drupal
  ? drupal.view.image
  : document?.cloudinaryHeroImage
    ? document?.cloudinaryHeroImage[0]?.url
    : ''

const savePostalAddress = ref<boolean>(false)

const minSum = isDebug.value
  ? ref<number>(1)
  : ref<number>(
      Number(drupal ? drupal.view.minSum : document?.minSum) || formDefaults?.sums?.minSum || 25,
    ) // Set minSum to 1 if debug, otherwise the cms value, or the default

const person = ref(
  zPerson.parse({
    name: { first: params.value?.fname ?? '', last: params.value?.lname ?? '' },
    contacts: { mobile: params.value?.mobile ?? '', email: params.value?.email ?? '' },
    ssn: params.value?.ssn ?? '',
    birthDate: params.value?.birthDate ?? '',
    address: {
      postcode: params.value?.postcode ?? '',
      countryCode:
        params.value?.countryCode && params.value?.countryCode !== ''
          ? params.value?.countryCode
          : 'SE',
    },
    hashId: params.value?.whId ?? '',
  }),
)
// Reached from a second ask (`?prefill=1`): prefill from the result-token cookie, not the URL.
usePrefillPerson(person)
const masked = ref(zPerson.parse({ ...person.value }))

const baseUrl = `${useRequestURL().protocol}//${useRequestURL().hostname}${useRequestURL().port === '80' || useRequestURL().port === '8080' ? '' : `:${useRequestURL().port}`}`
const resultUrls = useResultUrls({
  entity: person,
  resultDocuments: drupal
    ? {
        ok: { sys: { id: drupal.results.done ?? '' } } as ResultDocument,
        postal: { sys: { id: drupal.results.pending ?? '' } } as ResultDocument,
        aborted: { sys: { id: drupal.results.aborted ?? '' } } as ResultDocument,
      }
    : {
        ok: document!.thanksEsignOK,
        postal: document!.thanksPaperOK,
        aborted: document!.fallbackEsignAborted,
      },
  route,
  slug: drupal ? drupal.view.slug : slug,
  form: drupal ? { formId: drupal.formId, formLang: drupal.formLang, optedIn } : undefined,
  baseUrl,
  type: 'donation-monthly',
})

const handleForm = async () => {
  try {
    v$.value.$touch()
    if (v$.value.$invalid) {
      throw new Error('invalid')
    }
    formStatus.value = 'loading'

    // Create/update contact
    const contact = await $fetch('/api/contact', {
      method: 'PATCH',
      body: {
        ...person.value.contacts,
        ...person.value.name,
        ...person.value.address,
        ssn: person.value.ssn,
        birthDate: person.value.birthDate,
      },
    })
    if (!contact.success) {
      throw new Error('no.contact')
    }

    // register membership & leave consent pending
    const memRes = await $fetch('/api/djurensratt/membership/monthly', {
      method: 'POST',
      body: {
        partId: contact.partId,
        membership: {
          sum: sum.value.toString(),
          recruitmentId: campaignCode.value ? campaignCode.value : undefined,
          presentId:
            params.value.handedOut ||
            skipPresent.value ||
            presents.value.length === 0 ||
            isNoPresent(present.value)
              ? ''
              : presentCode(present.value),
        },
        consent: {},
        stats,
      },
    })

    if (!memRes.success) {
      throw new Error('no.membership')
    }

    // redirect to consent form
    const resolvedWhId = (route.query.whId as string) || contact.hashId || ''
    if (!resolvedWhId) {
      throw new Error('no.hashid')
    }

    const consentQuery = {
      ...route.query, // preserve existing query params
      ...{
        whId: resolvedWhId,
        sum: sum.value,
        // As linked: /medgivande looks Drupal up lowercased, Contentful as written.
        slug,
        type: 'donation-monthly',
        // Tells /medgivande the form came from Drupal, so losing it there is an error.
        formId: drupal?.formId,
        formLang: drupal?.formLang,
        ...(optedIn ? { [FORMS_OPTIN_PARAM]: FORMS_OPTIN_VALUE } : {}),
      },
    }

    formStatus.value = 'success'

    // Navigate to the consent form with the query params. `firstValues` keeps a repeated
    // param single — joining them here is what put "MNWKKT,PORTBLXLSCTA" in the CRM.
    const url = withQuery('/medgivande', firstValues(consentQuery))
    navigateTo(url, { external: true })
  } catch (e) {
    formStatus.value = 'error'
    reportError(e, 'handleForm in /manadsgivare', {
      route: route.fullPath,
      sum: sum.value,
      errorMessage: (e as any)?.message,
      errorStep: (() => {
        const message = (e as any)?.message
        if (message === 'no.contact') return 'contact'
        if (message === 'no.membership') return 'membership'
        if (message === 'no.hashid') return 'consent-redirect'
        return 'unknown'
      })(),
    })
  }
}

const rules = computed(() => {
  return {
    ...commonRules({
      person: person.value,
      t,
      siteLang: SITE_LANG,
      minSum: minSum.value,
      mobileRequired: true,
    }),
    ...(!params.value.handedOut && presents.value.length > 0
      ? presentRules({ t, handedOut: params.value.handedOut ?? false, skipGift: skipPresent.value })
      : {}),
  }
})

const v$ = useVuelidate(rules, {
  sum,
  personDetails: person,
  personPostal: person,
  gift: present,
})

const manualRedirect = (): void => {
  navigateTo('https://djurensratt.se/tekniskt-underhall/', { external: true, redirectCode: 302 })
}

onMounted(async () => {
  route.meta.debugSteps = debugSteps.value
  route.meta.debugUrls = resultUrls.value
  route.meta.formStatus = formStatus
  if (route.query?.ssn) {
    const ssn = route.query?.ssn as string
    const { default: Personnummer } = await import('personnummer')
    if (!Personnummer.valid(ssn)) {
      // reset state
      masked.value = zPerson.parse({ ...person.value })
      person.value.useLookup = false
    } else {
      showLoader.value = true
      const lookupResult = zSsnLookupRes.parse(
        await $fetch('/api/lookup/ssn', { method: 'POST', body: { ssn } }),
      )
      hasResult.value = lookupResult.success
      showLoader.value = false
      person.value.useLookup = lookupResult.success
      if (!lookupResult.success) {
        masked.value = zPerson.parse({ ...person.value })
        return
      }
      const { name, address } = lookupResult
      masked.value = zPerson.parse({
        ...person.value,
        name: {
          first: name.first,
          last: name.last,
        },
        address: {
          street: address.street,
          postcode: address.postcode,
          city: address.city,
          countryCode: address.countryCode || 'SE',
        },
      })
    }
  }

  // CRM Health Check
  try {
    const { crmHealthy, integrationMode } = await useCrmHealthCheck()
    if (!crmHealthy || integrationMode.value === 'manual' || document?.maintenance) {
      manualRedirect()
    }
  } catch (e) {
    flagError(e, '/ge-medlemskap - Error when checking CRM health', $rollbar)
  }
})

const pageTitle = drupal ? drupal.view.title : document?.title
const pageDescription = drupal ? drupal.view.leadText : document?.description
useHead({
  title: pageTitle,
  meta: [
    { name: 'description', content: pageDescription },
    ...(noindex ? [{ name: 'robots', content: 'noindex' }] : []),
  ],
})
useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  description: pageDescription,
  ogDescription: pageDescription,
  ogImage: cloudinaryStillUrl(imageUrl),
  twitterCard: 'summary_large_image',
})
</script>
